import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/utils/cn';
import { verifyAttrs } from '@shared/verify/core/contract';

interface ThreeDSChallengeOverlayProps {
  /** Controls visibility. The hook flips this true on 3DS-start, false on 3DS-end. */
  open: boolean;
}

/**
 * Full-screen modal that appears while the user's bank verifies the payment
 * via 3DS challenge. Locked design from T30 (plan `remove-confirm-step.md`,
 * Decision D2 + A1):
 *
 * - Renders as a Radix Dialog with `modal={true}` — backdrop dims the
 *   underlying page; click-through disabled; focus traps inside.
 * - No close button — the overlay is dismissed only by the commit
 *   completing (the parent unmounts it via the `open` prop). Bypasses
 *   shadcn's wrapper (which hardcodes an X) by using Radix primitives
 *   directly.
 * - Spinner + title + description. Title/description use i18n keys
 *   THREEDS_OVERLAY_TITLE and THREEDS_OVERLAY_DESCRIPTION (errors namespace).
 * - `aria-live="polite"` on the description so screen readers announce
 *   the verification is happening on open.
 * - On mount, focus moves into the dialog. On unmount, Radix restores
 *   focus to the previously-focused element (the commit button).
 *
 * The overlay should be rendered by whichever page hosts useReservationCommit
 * (today: ConfirmationPage; post-flip: Guarantee/Personalize). The page wires
 * `open` to the hook's `isHandling3ds` state.
 */
export function ThreeDSChallengeOverlay({ open }: ThreeDSChallengeOverlayProps) {
  const { t } = useTranslation('errors');

  return (
    <DialogPrimitive.Root open={open} modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/80',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          data-embed-overlay=""
          // Holds a minimum frame height while the bank's challenge is
          // pending. Stripe injects that challenge as its own iframe, which we
          // can neither measure nor style — this overlay is open for exactly
          // the same window, so it stands in as the floor. See
          // `html[data-embed-3ds]` in src/index.css.
          data-embed-3ds=""
          role="alertdialog"
          className={cn(
            'fixed left-[50%] top-[50%] z-50',
            'translate-x-[-50%] translate-y-[-50%]',
            'w-full max-w-md',
            'bg-background text-foreground',
            'border rounded-xl shadow-lg',
            'p-8',
            'flex flex-col items-center justify-center gap-4 text-center',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
          // No close handlers — overlay can only be dismissed by parent
          // controlling `open`. Block escape + outside-click so a careful user
          // can't dismiss while their bank is verifying.
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          {...verifyAttrs('ThreeDSChallengeOverlay', { isOpen: open })}
        >
          <Loader2 className="h-10 w-10 animate-spin text-brand" aria-hidden="true" />
          <DialogPrimitive.Title className="font-display text-xl font-semibold">
            {t('THREEDS_OVERLAY_TITLE')}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description
            className="text-sm text-foreground/70"
            aria-live="polite"
          >
            {t('THREEDS_OVERLAY_DESCRIPTION')}
          </DialogPrimitive.Description>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
